import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';
import { smsNotificationService } from '../services/smsNotificationService';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  const [happyTravelers, setHappyTravelers] = useState(0);
  const [tripsPlanned, setTripsPlanned] = useState(0);
  const [loading, setLoading] = useState(true);

  // SMS signup state
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSuccess, setSmsSuccess] = useState(false);
  const [smsError, setSmsError] = useState('');

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        // Fetch happy travelers count
        const { data: travelersData, error: travelersError } = await supabase?.rpc('get_happy_travelers_count');

        if (travelersError) {
          console.error('Error fetching happy travelers:', travelersError);
        } else {
          setHappyTravelers(travelersData || 0);
        }

        // Fetch trips planned count
        const { data: tripsData, error: tripsError } = await supabase?.rpc('get_trips_planned_count');

        if (tripsError) {
          console.error('Error fetching trips planned:', tripsError);
        } else {
          setTripsPlanned(tripsData || 0);
        }
      } catch (err) {
        console.error('Error fetching statistics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  const handleSmsSignup = async (e) => {
    e?.preventDefault();
    setSmsLoading(true);
    setSmsError('');
    setSmsSuccess(false);

    try {
      await smsNotificationService?.subscribe(phoneNumber, countryCode);
      setSmsSuccess(true);
      setPhoneNumber('');

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSmsSuccess(false);
      }, 5000);
    } catch (error) {
      setSmsError(error?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setSmsLoading(false);
    }
  };

  return (
    <footer className="bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Main Footer Content - Reorganized Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-0">
          {/* Brand Section - Takes 2 columns on desktop */}
          <div className="md:col-span-2">
            <div className="flex items-start space-x-3 mb-0">
              <Link to="/landing-page" className="flex items-center flex-shrink-0">
                <img
                  src="/assets/images/cff9bdc7-c1fa-4ec7-ab24-15e410b9d502-1764088362619.png"
                  alt="Bear ranger mascot logo"
                  className="h-48 w-48 object-contain" />

              </Link>
              <p className="font-body text-background/80 text-sm pt-1">Discover the easiest way to plan incredible state-park adventures. ParkTrip Planner AI crafts fully personalized itineraries using advanced AI; helping you explore more, stress less, and enjoy the outdoors. Built with passion by a one-man team.

              </p>
            </div>
          </div>

          {/* Legal Section - Trust Indicators moved here */}
          <div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="font-body text-background/80 text-sm">SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-orange-500" />
                <span className="font-body text-background/80 text-sm">
                  {loading ? 'Loading...' : `${happyTravelers?.toLocaleString()} Happy Travelers`}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-yellow-500" />
                <span className="font-body text-background/80 text-sm">
                  {loading ? 'Loading...' : `${tripsPlanned?.toLocaleString()} Trips Planned`}
                </span>
              </div>
            </div>
          </div>

          {/* SMS Notification - Moved to right column */}
          <div>
            <h3 className="font-heading font-heading-bold text-sm text-background uppercase tracking-wide mb-2">
              Stay Updated
            </h3>
            <div className="bg-primary/10 rounded-lg p-2.5 border border-primary/20">
              <div className="flex items-center space-x-2 mb-1.5">
                <Icon name="Smartphone" size={16} className="text-yellow-500" />
                <p className="font-body font-body-bold text-background text-xs">
                  SMS Features
                </p>
              </div>
              <p className="font-body text-background/70 text-xs mb-2">
                Get notified when SMS features launch!
              </p>
              
              <form onSubmit={handleSmsSignup} className="space-y-1.5">
                {/* Country Code + Phone Number Row */}
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e?.target?.value)}
                    className="px-2 py-2 rounded-lg border border-primary/30 bg-background text-foreground 
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                             text-xs font-body w-20"








                    disabled={smsLoading || smsSuccess}>

                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+7">🇷🇺 +7</option>
                    <option value="+52">🇲🇽 +52</option>
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+82">🇰🇷 +82</option>
                  </select>

                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e?.target?.value)}
                    placeholder="Phone"
                    className="flex-1 px-2 py-2 rounded-lg border border-primary/30 bg-background text-foreground 
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                             placeholder:text-foreground/50 text-xs font-body"








                    disabled={smsLoading || smsSuccess}
                    required />

                </div>

                {/* Submit Button - Full Width */}
                <button
                  type="submit"
                  disabled={smsLoading || smsSuccess || !phoneNumber}
                  className="w-full px-3 py-2 bg-primary text-white rounded-lg font-body font-body-bold text-xs
                           hover:bg-primary/90 transition-organic disabled:opacity-50 
                           disabled:cursor-not-allowed flex items-center justify-center space-x-1">









                  {smsLoading ?
                  <>
                      <Icon name="Loader" size={12} className="animate-spin" />
                      <span>Subscribing...</span>
                    </> :
                  smsSuccess ?
                  <>
                      <Icon name="Check" size={12} />
                      <span>Subscribed!</span>
                    </> :

                  <>
                      <Icon name="Bell" size={12} />
                      <span>Notify Me</span>
                    </>
                  }
                </button>
              </form>

              {/* Error Message */}
              {smsError &&
              <div className="mt-1.5 flex items-center space-x-1 text-red-500">
                  <Icon name="AlertCircle" size={12} />
                  <p className="font-body text-xs">{smsError}</p>
                </div>
              }

              {/* Success Message */}
              {smsSuccess &&
              <div className="mt-1.5 flex items-center space-x-1 text-success">
                  <Icon name="CheckCircle" size={12} />
                  <p className="font-body text-xs">
                    You'll be notified!
                  </p>
                </div>
              }
            </div>
          </div>
        </div>

        {/* Bottom Bar - Privacy and Terms inline with copyright */}
        <div className="border-t border-background/20 pt-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
            <p className="font-caption text-sm text-background/60">
              © {currentYear} All rights reserved.
            </p>
            <span className="hidden sm:inline text-background/40">•</span>
            <div className="flex items-center gap-4">
              <Link
                to="/privacy-policy"
                className="font-caption text-sm text-background/60 hover:text-background transition-organic">

                Privacy Policy
              </Link>
              <span className="text-background/40">•</span>
              <Link
                to="/terms-of-service"
                className="font-caption text-sm text-background/60 hover:text-background transition-organic">

                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;